/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect } from 'react';
import mbpLogger from '@module-logger/mbp';
import {
    object, string, shape, bool,
} from 'prop-types';
import { useQuery } from '@apollo/client';
import { useDispatch } from 'react-redux';
import Media from 'react-media';
import ReactHTMLParser from 'react-html-parser';
import { Grid, makeStyles } from '@material-ui/core';

import PDPSkeleton from '../../../GraphqlSkeletonComponents/PDPSkeleton';
import MobilePDPSkeleton from '../../../GraphqlSkeletonComponents/MobileSkeleton/MobilePDPSkeleton';
import { trackData } from '../../../../../helpers/tracking/common/commonTrackingHelpers';
import { getProductDetailBySKU } from '../../../../../pages/Account/services/graphQL';
import { GRAPHQL_ENV } from '../../../../../gql';
import ProductNotAvailablePage from './ProductNotAvailablePage';
import GraphqlSeoHeadTags from '../../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import Graphql404ErrorProductContainer from './Graphql404ErrorProductContainer';
import { emitErrorTrackingEvent } from '../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const useStyles = makeStyles((theme) => ({
    header: {
        WebkitFontSmoothing: 'antialiased',
        textAlign: 'center',
        width: '100%',
    },
    oopsContainer: {
        lineHeight: '1.2',
        borderTop: `1px solid ${theme.palette.colorNeutral20}`,
        padding: '20px 0 20px 0',
        '& h3': {
            margin: '0 0 5px 0',
        },
    },
    oopsText: {
        color: theme.palette.colorNeutral70,
        fontFamily: 'Roboto, sans-serif',
        fontSize: '16px',
        letterSpacing: '.03em',
        textShadow: '1px 1px 1px rgba(0, 0, 0, 0.004)',
    },
    break: {
        height: '12px',
        background: theme.palette.colorNeutral30,
        boxShadow: 'inset 0 1px 3px rgba(59,33,81,0.21), inset 0 1px 2px rgba(59,33,81,0.21)',
        [theme.breakpoints.up(1024)]: {
            margin: '0 15%',
        },
    },
    bestSellerContainer: {
        padding: '15px',
    },
    bestSellersText: {
        color: theme.palette.black,
        fontSize: '18px',
        fontWeight: 'normal',
        fontFamily: 'LatoMedium,sans-serif',
        fontStyle: 'normal',
        [theme.breakpoints.down('xs')]: {
            fontSize: '14px',
        },
    },
}));

const ProductNotAvailableContainer = ({
    brand, seoData, code, productUnavailableName,
}) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(emitErrorTrackingEvent({
            action: 'Error',
            category: 'Errors',
            label: 'Product Not Available',
            tag: code,
            products: code ? [{ baseCode: code }] : [],
            page: { type: 'product' },
            error: { errorMessage: 'Product Not Available' },
        }));
    }, [code, dispatch]);

    trackData({
        action: 'track_404_data',
    });

    const SOLD_OUT_PRODUCT_QUERY = getProductDetailBySKU(code, brand['domain-name'], GRAPHQL_ENV);
    const { data, error, loading } = useQuery(SOLD_OUT_PRODUCT_QUERY);
    if (loading) {
        return (
            <Media
                query="(min-width: 599px)"
            >
                {(matches) => (matches
                    ? <PDPSkeleton />
                    : <MobilePDPSkeleton />

                )}
            </Media>
        );
    }
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            query: SOLD_OUT_PRODUCT_QUERY,
            component: 'ProductNotAvailableContainer.js',
            message: 'Error loading data from Graphql',
            env: GRAPHQL_ENV,
            error,
        });
    }

    // if the user direct loads a product not available
    // to prevent a blank product page when the app rehydrates client side
    // window.is404 is set to true.
    // If CoreBrowsingBuilder sees that window property set to true
    // it renders the product not available page
    // Becasue this value was previously never being set to false after it was initially set to true from ssr
    // if the user got a PNA view from SSR then navigated to any other product PNA view was persisting
    // this sets the window value to false and other pdps will load
    if (typeof window !== 'undefined' && window.is404) {
        window.is404 = false;
    }

    // with left container
    if (data?.findProductByPartNumber) {
        return (
            <ProductNotAvailablePage
                data={data?.findProductByPartNumber}
                brand={brand}
                seoData={seoData}
            />
        );
    }

    // without left container
    return (
        <>
            {
                seoData?.seo_searchable
                    ? (
                        <GraphqlSeoHeadTags seoData={seoData} />
                    ) : null
            }
            <Grid className={classes.header}>
                <Grid className={classes.oopsContainer}>
                    {productUnavailableName ? <h3>{ReactHTMLParser(productUnavailableName)}</h3> : null}
                    <div className={classes.oopsText}>We are sorry, the product you have selected is not available. Please select from the list of products below.</div>
                </Grid>
                <div className={classes.break} />
                <Grid className={classes.bestSellerContainer}>
                    <span className={classes.bestSellersText}>
                        Trending Products
                    </span>
                </Grid>
            </Grid>
            <Graphql404ErrorProductContainer brand={brand} eventSubType="productNotAvailable" />

        </>
    );
};

ProductNotAvailableContainer.propTypes = {
    productUnavailableName: string.isRequired,
    brand: object.isRequired,
    code: string.isRequired,
    seoData: shape({
        keywords: string.isRequired,
        meta_description: string.isRequired,
        page_title: string.isRequired,
        seo_searchable: bool.isRequired,
    }).isRequired,
};

export default ProductNotAvailableContainer;
